import { useState, useEffect, useCallback } from 'react';
import { MATURITY_LEVELS } from '../workerConfig';

export interface FeatureFlag {
  key: string;
  name: string;
  description: string;
  enabled: boolean;
  rolloutPercent: number;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface FeatureFlags {
  darkMode: boolean;
  aiSearch: boolean;
  betaDashboard: boolean;
  animatedHero: boolean;
  compactCards: boolean;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  allFlags: FeatureFlag[];
}

const FLAG_API = `${MATURITY_LEVELS.l2}/api/flags`;

export function useFeatureFlags(): FeatureFlags {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFlags = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(FLAG_API, { signal: AbortSignal.timeout(5000) });
      if (!res.ok) throw new Error('Failed to fetch flags');
      const data = await res.json();
      setFlags(data.flags ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
      setFlags([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFlags();
    // Poll every 10 seconds to pick up flag changes
    const interval = setInterval(fetchFlags, 10000);
    return () => clearInterval(interval);
  }, [fetchFlags]);

  const getFlag = (key: string): boolean => {
    const flag = flags.find((f) => f.key === key);
    if (!flag) return false;
    // If rolloutPercent < 100, use a deterministic check based on session
    if (flag.rolloutPercent < 100) {
      const sessionId = sessionStorage.getItem('flagSessionId') ?? (() => {
        const id = Math.random().toString(36).slice(2);
        sessionStorage.setItem('flagSessionId', id);
        return id;
      })();
      // Simple hash to get a number 0-99
      const hash = sessionId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 100;
      return flag.enabled && hash < flag.rolloutPercent;
    }
    return flag.enabled;
  };

  return {
    darkMode: getFlag('dark-mode'),
    aiSearch: getFlag('ai-search'),
    betaDashboard: getFlag('beta-dashboard'),
    animatedHero: getFlag('animated-hero'),
    compactCards: getFlag('compact-cards'),
    loading,
    error,
    refresh: fetchFlags,
    allFlags: flags,
  };
}
